import type { ProviderSlot } from "@src/interfaces/provider.interface";

const providerDatesCache = new Map<string, { isoDates: string[]; cachedAtMs: number }>();
const providerDatesErrorCache = new Map<string, { errorMessage: string; cachedAtMs: number }>();
const providerSlotsCache = new Map<string, { slots: ProviderSlot[]; cachedAtMs: number }>();
const PROVIDER_DATES_CACHE_TTL_MS = 5 * 60 * 1000;
const PROVIDER_SLOTS_CACHE_TTL_MS = 5 * 60 * 1000;
const PROVIDER_DATES_ERROR_CACHE_TTL_MS = 5 * 60 * 1000;

const buildDatesKey = (providerId: string, careFormat: string, cacheScope: string): string => {
  return `${providerId}:${careFormat}:${cacheScope}`;
};

const buildSlotsKey = (
  providerId: string,
  isoDate: string,
  careFormat: string,
  cacheScope: string,
): string => {
  return `${providerId}:${isoDate}:${careFormat}:${cacheScope}`;
};

export const getCachedProviderDates = (
  providerId: string,
  careFormat: string,
  cacheScope: string,
): string[] | null => {
  const key = buildDatesKey(providerId, careFormat, cacheScope);
  const cachedDates = providerDatesCache.get(key);

  if (!cachedDates) {
    return null;
  }

  if (Date.now() - cachedDates.cachedAtMs > PROVIDER_DATES_CACHE_TTL_MS) {
    providerDatesCache.delete(key);
    return null;
  }

  return cachedDates.isoDates;
};

export const setCachedProviderDates = (
  providerId: string,
  careFormat: string,
  cacheScope: string,
  isoDates: string[],
): void => {
  const key = buildDatesKey(providerId, careFormat, cacheScope);
  providerDatesCache.set(key, {
    isoDates,
    cachedAtMs: Date.now(),
  });
  providerDatesErrorCache.delete(key);
};

export const getCachedProviderDatesError = (
  providerId: string,
  careFormat: string,
  cacheScope: string,
): string | null => {
  const key = buildDatesKey(providerId, careFormat, cacheScope);
  const cachedError = providerDatesErrorCache.get(key);

  if (!cachedError) {
    return null;
  }

  if (Date.now() - cachedError.cachedAtMs > PROVIDER_DATES_ERROR_CACHE_TTL_MS) {
    providerDatesErrorCache.delete(key);
    return null;
  }

  return cachedError.errorMessage;
};

export const setCachedProviderDatesError = (
  providerId: string,
  careFormat: string,
  cacheScope: string,
  errorMessage: string,
): void => {
  const key = buildDatesKey(providerId, careFormat, cacheScope);
  providerDatesErrorCache.set(key, {
    errorMessage,
    cachedAtMs: Date.now(),
  });
  providerDatesCache.delete(key);
};

export const getCachedProviderSlots = (
  providerId: string,
  isoDate: string,
  careFormat: string,
  cacheScope: string,
): ProviderSlot[] | null => {
  const key = buildSlotsKey(providerId, isoDate, careFormat, cacheScope);
  const cachedSlots = providerSlotsCache.get(key);

  if (!cachedSlots) {
    return null;
  }

  if (Date.now() - cachedSlots.cachedAtMs > PROVIDER_SLOTS_CACHE_TTL_MS) {
    providerSlotsCache.delete(key);
    return null;
  }

  return cachedSlots.slots;
};

export const setCachedProviderSlots = (
  providerId: string,
  isoDate: string,
  careFormat: string,
  cacheScope: string,
  slots: ProviderSlot[],
): void => {
  providerSlotsCache.set(buildSlotsKey(providerId, isoDate, careFormat, cacheScope), {
    slots,
    cachedAtMs: Date.now(),
  });
};

export const invalidateProviderAvailabilityCache = (): void => {
  providerDatesCache.clear();
  providerDatesErrorCache.clear();
  providerSlotsCache.clear();
};
